import React, { useMemo } from "react";
import { IExcel } from "../../../common/batchNotificationInterface";
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "../../../common/MaterialComponent";
import {
	GetExcelDataColumn,
	GetHeaderValue,
} from "../../../hooks/useBatchGroupConfiguration";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import styles from "../GroupConfiguration/GroupConfiguration.style";
import { Alert } from "@material-ui/lab";

export interface GroupDetailTablePreviewProps {
	excelData: IExcel;
	onExcelDataUpdate: (excelData: IExcel) => void;
}

export const GroupDetailTablePreview: React.FC<GroupDetailTablePreviewProps> = ({
	excelData,
	onExcelDataUpdate,
}) => {
	const strings = useGetComponentStringsContext().GroupDetailTablePreview;
	const classes = styles();

	const getColumnIndex = (): number => {
		if (excelData.selectedColumn && excelData.isHeaderIncluded) {
			return excelData.header.findIndex(
				(x: any) => x === excelData.selectedColumn
			);
		} else if (excelData.selectedColumn) {
			return excelData.staticHeader!.findIndex(
				(x: any) => x === excelData.selectedColumn
			);
		}
		return 0;
	};

	const getHeaderValue = (
		isHeaderIncluded: boolean = false,
		staticHeader: string[] = [],
		excelData: IExcel
	): string[] => {
		const headerList = GetHeaderValue(
			isHeaderIncluded,
			staticHeader,
			excelData
		);
		return headerList && headerList.length > 0 ? headerList : [];
	};

	const onColumnValueChangeHandler = (
		event: React.ChangeEvent<{ value: unknown }>
	) => {
		const excelDataDetail = { ...excelData };
		excelDataDetail.selectedColumn = event.target.value as string;
		onExcelDataUpdate(excelDataDetail);
	};

	const onVariableChangeHandler = (
		event: React.ChangeEvent<{ value: unknown }>,
		variable: string
	) => {
		const excelDataDetail = { ...excelData };
		excelDataDetail.variableMapping[variable] = event.target.value as string;
		onExcelDataUpdate(excelDataDetail);
	};

	const isHeaderIncludedChangedHandler = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const excelDataDetail: IExcel = { ...excelData };
		excelDataDetail.isHeaderIncluded = event.target.checked;
		excelDataDetail.contentData = GetExcelDataColumn(excelDataDetail);
		if (
			excelDataDetail.isHeaderIncluded &&
			excelDataDetail.header &&
			excelDataDetail.header.length > 0
		) {
			excelDataDetail.selectedColumn = excelDataDetail.header[getColumnIndex()];
		} else {
			excelDataDetail.selectedColumn = excelDataDetail.staticHeader![
				getColumnIndex()
			];
		}
		onExcelDataUpdate(excelDataDetail);
	};

	const headers = useMemo(() => {
		return getHeaderValue(
			excelData.isHeaderIncluded,
			excelData.staticHeader,
			excelData
		);
	}, [excelData]);

	return (
		<>
			<Grid
				item
				container
				xs={12}
				className={classes.tableConfiguration}
				data-testid="tableContainer"
				spacing={1}
			>
				<Grid item xs={6}>
					{Object.entries(excelData.variableMapping).find(
						(entry) => entry[0] === entry[1]
					) && (
						<Box padding={"0.5rem"}>
							<Alert severity="warning" variant="outlined">
								{strings.missingVariables}
							</Alert>
						</Box>
					)}
					<Box padding={"0.5rem"}>
						<FormControl className={classes.columnSelect} fullWidth>
							<InputLabel>{strings.selectPhoneColumnLabel}</InputLabel>
							<Select
								data-testid="columnSelect"
								value={excelData.selectedColumn}
								onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
									onColumnValueChangeHandler(event)
								}
							>
								{headers.map((option: any, id: number) => (
									<MenuItem key={id} value={option}>
										<ListItemText primary={option} />
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					{Object.keys(excelData.variableMapping).map((variable, index) => (
						<Box padding={"0.5rem"}>
							<FormControl
								key={index}
								className={classes.columnSelect}
								fullWidth
							>
								<InputLabel>{`${
									strings.genericSelectColumnLabel
								} ${variable.replace("[[", "").replace("]]", "")}`}</InputLabel>
								<Select
									required
									data-testid="columnSelect"
									value={
										headers.includes(excelData.variableMapping[variable])
											? excelData.variableMapping[variable]
											: ""
									}
									onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
										onVariableChangeHandler(event, variable)
									}
								>
									{headers.map((option: any, id: number) => (
										<MenuItem key={id} value={option}>
											<ListItemText primary={option} />
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					))}
				</Grid>
				<Grid item xs={6}>
					<FormControlLabel
						className={classes.headerCheckBox}
						control={
							<Checkbox
								data-testid="headerCheckBox"
								color="primary"
								checked={excelData.isHeaderIncluded}
								onChange={(event: any) => isHeaderIncludedChangedHandler(event)}
							/>
						}
						label={strings.tableHeaderIncludedLabel}
					/>
					<Paper className={classes.tableGrid} data-testid="excelData">
						<Grid item xs={12}>
							<Table aria-label="group phone number">
								<TableHead>
									<TableRow>
										{getHeaderValue(
											excelData.isHeaderIncluded,
											excelData.staticHeader,
											excelData
										).map((name: any) => (
											<TableCell
												align="center"
												key={name}
												className={
													classes.tableCell +
													" " +
													(excelData.selectedColumn === name
														? classes.highlight
														: "")
												}
											>
												{name}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{excelData.contentData &&
										excelData.contentData.length > 0 &&
										excelData.contentData
											.slice(0, 10)
											.map((row: any, rowIndex: number) => (
												<TableRow key={rowIndex}>
													{Object.values(row).length > 0 &&
														Object.values(row).map(
															(name: any, indexId: any) => (
																<TableCell
																	align="center"
																	key={indexId}
																	className={
																		classes.tableCell +
																		" " +
																		(indexId === getColumnIndex()
																			? classes.highlight
																			: "")
																	}
																>
																	{name ? name : "-"}
																</TableCell>
															)
														)}
												</TableRow>
											))}
								</TableBody>
							</Table>
						</Grid>
					</Paper>
				</Grid>
			</Grid>
		</>
	);
};
